import type { TPartialProject } from "@/types/project";
import type { TSurfaceAvailability } from "@/types/integration-capabilities";
import type { TWorkspacePermission } from "./workspace-permissions";

export type TProjectSettingsTabKey = "general" | "members" | "workflow";
/** Project tabs that report an outcome rather than configure one (NG-1557). */
export type TProjectOutcomeTabKey = "delivery" | "results";

export interface IProjectTab<TKey extends string> {
  key: TKey;
  labelKey: string;
  path: string;
  required: TWorkspacePermission[];
  /** LaunchDarkly key that must be ON for this tab to exist. Absent means always. */
  flag?: string;
}

export type IProjectSettingsTab = IProjectTab<TProjectSettingsTabKey>;

export const PROJECT_SETTINGS_TABS: IProjectSettingsTab[] = [
  {
    key: "general",
    labelKey: "project_settings.tabs.general",
    path: "",
    required: ["project.edit"],
  },
  {
    key: "members",
    labelKey: "project_settings.tabs.members",
    path: "members",
    required: ["project.edit"],
  },
  {
    key: "workflow",
    labelKey: "project_settings.tabs.workflow",
    path: "workflow",
    required: ["project.edit"],
  },
];

/** Project TABS gated on more than project access — Delivery and Results (NG-1557).
 *
 *  They were settings tabs until NG-1557 moved them: both are read surfaces for someone
 *  looking at a project rather than configuring it, and Settings is where you configure.
 *  The gating is unchanged by the move and is stated here so the tab row in
 *  `routes/project/tabs-layout.tsx` and the route guards agree on one definition.
 *
 *  `project.read` for both, matching the endpoints that feed them: `GET .../lms-links/`
 *  and `GET .../lms-results/` are both `project.read`, so what delivers a project and
 *  what it achieved are readable by anyone who can see the project. Only the picker, the
 *  two link writes and the recompute are `project.edit`, and each page hides its own
 *  write controls — gating the tabs on `project.edit` would lock a reader out of data
 *  the API is happy to hand them.
 *
 *  No `flag` on either: both sat behind the 360Learning flag until it shipped to
 *  everyone and was retired (NG-2072). The connector gate (NG-2265) still applies, in
 *  `getVisibleProjectOutcomeTabs` below and in the route's `useOutcomeTabGate`.
 */
const PROJECT_OUTCOME_TABS: IProjectTab<TProjectOutcomeTabKey>[] = [
  {
    key: "delivery",
    labelKey: "project.tabs.delivery",
    path: "delivery",
    required: ["project.read"],
  },
  {
    key: "results",
    labelKey: "project.tabs.results",
    path: "results",
    required: ["project.read"],
  },
];

export function hasProjectPermission(
  project: TPartialProject | null | undefined,
  permission: TWorkspacePermission,
): boolean {
  return project?.permissions?.includes(permission) ?? false;
}

/**
 * Whether the viewer can read `project` but not edit it (NG-1876): its
 * `permissions` have arrived and hold no `project.edit`, the action the PATCH
 * enforces. `permissions` absent means the verdict isn't in yet, which is not a
 * no, so a surface gated on this can't show a read-only state that then vanishes.
 * The overview's banner, its properties panel, its description and the team
 * project drawer all read this one verdict.
 */
export function isProjectReadOnly(project: TPartialProject | null | undefined): boolean {
  return project?.permissions !== undefined && !hasProjectPermission(project, "project.edit");
}

/**
 * Capability gating milestone CREATE in a project (NG-2096, sheet row 96,
 * backend db/0458): the real ``milestone.create`` permission — held by the
 * Admin and Standard System Roles (Default On), grantable in custom roles,
 * and conferred by the Project Lead object role on the lead's own project.
 * Surfaced in the project/work-item `permissions` array like every other
 * grant. The Create Work Item modal reads this for the Milestone type option;
 * the API stays authoritative (a relational exception — a Project Lead's
 * manager, #2822/#2825 — can create without the key being surfaced here, and
 * gets the same treatment the milestone.edit capability has always gotten:
 * the backend 201s what the UI did not offer). Editing an existing milestone
 * keeps the stricter milestone.edit rule (backend ``can_edit_milestone``);
 * no UI surface gates on it yet, so it carries no helper of its own.
 */
const MILESTONE_CREATE_CAPABILITY = "milestone.create";

export function canCreateMilestones(project: TPartialProject | null | undefined): boolean {
  return project?.permissions?.includes(MILESTONE_CREATE_CAPABILITY) ?? false;
}

export function hasAnyProjectPermission(
  project: TPartialProject | null | undefined,
  permissions: TWorkspacePermission[],
): boolean {
  if (!project?.permissions?.length) return false;
  return permissions.some((p) => project.permissions!.includes(p));
}

/** The flag a tab is gated behind, if any.
 *
 *  Exists so a caller can tell "this tab is hidden because the flag is off" apart from
 *  "this tab is hidden because the flags have not loaded yet" — the distinction the
 *  settings layout has to make before it redirects, and the one NG-758 fixed on the
 *  workspace side after a deep link to a gated tab bounced.
 */
export function projectSettingsTabFlag(key: TProjectSettingsTabKey): string | undefined {
  return PROJECT_SETTINGS_TABS.find((tab) => tab.key === key)?.flag;
}

/** Which of `tabs` this viewer may see: permitted, and not hidden by an off flag.
 *
 *  One predicate for both rows. The settings row and the outcome row asked the same
 *  question in the same words, and a filter that decides who sees what should not be
 *  written twice — the next change to it would otherwise have to find both copies.
 */
function visibleTabs<TKey extends string>(
  tabs: readonly IProjectTab<TKey>[],
  project: TPartialProject | null | undefined,
  isFlagEnabled: (key: string) => boolean,
): IProjectTab<TKey>[] {
  return tabs.filter(
    (tab) =>
      hasAnyProjectPermission(project, tab.required) &&
      (tab.flag === undefined || isFlagEnabled(tab.flag)),
  );
}

/** Fails closed on the flag predicate: a caller that forgets it under-shows a gated tab
 *  rather than shipping a dark surface to every project. The default lives at this entry
 *  point rather than in `visibleTabs`, because this is where a caller can omit it. The
 *  outcome twin below takes both its arguments as required instead (NG-2265). */
export function getVisibleProjectSettingsTabs(
  project: TPartialProject | null | undefined,
  isFlagEnabled: (key: string) => boolean = () => false,
): IProjectSettingsTab[] {
  return visibleTabs(PROJECT_SETTINGS_TABS, project, isFlagEnabled);
}

/** Every outcome tab's route path, in row order.
 *
 *  For the route-reachability guard, which has to see every tab whatever it requires.
 *  Deriving that list through `getVisibleProjectOutcomeTabs` needs a project to filter
 *  against, and a tab requiring anything other than the permission that project happens
 *  to hold drops out silently — the guard then stops covering it while still passing.
 */
export const PROJECT_OUTCOME_TAB_PATHS: readonly string[] = PROJECT_OUTCOME_TABS.map(
  (tab) => tab.path,
);

/** The flag behind an outcome tab, for the same tell-apart reason as the settings one. */
export function projectOutcomeTabFlag(key: TProjectOutcomeTabKey): string | undefined {
  return PROJECT_OUTCOME_TABS.find((tab) => tab.key === key)?.flag;
}

/** Delivery and Results, filtered for who is looking, what is switched on, and whether
 *  the workspace has a connector to report from.
 *
 *  Same predicate and the same fail-closed flag default as the settings row above, plus
 *  `deliveryResults`, from `useProjectOutcomeAvailability` (NG-2265, NG-2305). Both tabs report
 *  what a delivery/results connector supplies, so the condition is the row's rather than
 *  a tab's, and only `open` shows them: `pending` leaves them out until it resolves, so a
 *  tab is never drawn and then taken away. Required rather than defaulted, because both
 *  possible defaults are wrong for someone: `closed` hides Delivery from every customer
 *  with a connector, `open` ignores the gate.
 */
export function getVisibleProjectOutcomeTabs(
  project: TPartialProject | null | undefined,
  isFlagEnabled: (key: string) => boolean,
  deliveryResults: TSurfaceAvailability,
): IProjectTab<TProjectOutcomeTabKey>[] {
  if (deliveryResults !== "open") return [];
  return visibleTabs(PROJECT_OUTCOME_TABS, project, isFlagEnabled);
}

export function getProjectSettingsTabFromPath(pathname: string): TProjectSettingsTabKey {
  if (pathname.includes("/settings/members")) return "members";
  if (pathname.includes("/settings/workflow")) return "workflow";
  return "general";
}

export function hasProjectSettingsAccess(
  project: TPartialProject | null | undefined,
  tabKey: TProjectSettingsTabKey,
): boolean {
  const tab = PROJECT_SETTINGS_TABS.find((item) => item.key === tabKey);
  return tab ? hasAnyProjectPermission(project, tab.required) : false;
}
